import * as React from 'react';
import { Plus, ClipboardList, Zap, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { TaskCard } from './task-card';
import type { KanbanColumnProps } from '@/types';

// ============================================================================
// STYLES (light+dark safe)
// ============================================================================

const columnConfig: Record<
  'todo' | 'doing' | 'done',
  {
    icon: React.ReactElement;
    accentText: string;
    headerTint: string;
    dropTint: string;
  }
> = {
  todo: {
    icon: <ClipboardList className="h-5 w-5" />,
    accentText: 'text-muted-foreground',
    headerTint: '',
    dropTint: '',
  },
  doing: {
    icon: <Zap className="h-5 w-5" />,
    accentText: 'text-primary',
    headerTint: '',
    dropTint: '',
  },
  done: {
    icon: <CheckCircle2 className="h-5 w-5" />,
    accentText: 'text-emerald-600 dark:text-emerald-400',
    headerTint: '',
    dropTint: '',
  },
};

// ============================================================================
// MAIN COLUMN
// ============================================================================

export function KanbanColumn({
  title,
  statusKey,
  tasks,
  onTaskDrop,
  onTaskClick,
  onEditTask,
  onDeleteTask,
  onDuplicateTask,
  onAssigneeToggle,
  isEmpty = false,
  onAddClick,
  allowAddTask = true,
  draggingTaskId,
  onDragStart,
  onDragEnd,
}: KanbanColumnProps) {
  const [isDragOver, setIsDragOver] = React.useState(false);

  const config = columnConfig[statusKey];

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId && onTaskDrop) onTaskDrop(parseInt(taskId), statusKey);
  };

  return (
    <>
      <div
        className={cn(
          'flex flex-col h-full min-h-[600px] rounded-[1.5rem] border transition-all duration-300 overflow-hidden',
          isDragOver
            ? 'border-primary shadow-lg bg-primary/5'
            : 'border-border/50 shadow-sm'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Column Header */}
        <div className={cn('p-4 border-b border-border/10 bg-muted/5')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'flex items-center justify-center w-7 h-7 rounded-lg bg-background shadow-sm border border-border/40 text-muted-foreground/70',
                  config.accentText
                )}
              >
                {React.cloneElement(config.icon as any, {
                  className: 'h-4 w-4',
                })}
              </div>
              <div>
                <h3 className="text-xs font-semibold tracking-tight text-foreground/80">
                  {title}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] font-medium text-muted-foreground/50 leading-none">
                    {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
                  </span>
                </div>
              </div>
            </div>

            {allowAddTask && statusKey === 'todo' && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 rounded-lg hover:bg-primary/10 hover:text-primary transition-all duration-300"
                onClick={() => onAddClick?.(statusKey)}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Tasks Area */}
        <div className="flex-1 p-3 overflow-y-auto custom-scrollbar ">
          <div className="space-y-4">
            {tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center opacity-40">
                <div className="w-12 h-12 rounded-2xl bg-muted/20 flex items-center justify-center mb-3">
                  {React.cloneElement(config.icon as any, {
                    className: 'h-5 w-5 opacity-20',
                  })}
                </div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                  {isEmpty ? 'No activity' : 'Queue Empty'}
                </p>
                {allowAddTask && statusKey === 'todo' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-4 text-[10px] h-7 px-3 border border-dashed border-border/40 hover:border-solid hover:bg-muted/30"
                    onClick={() => onAddClick?.(statusKey)}
                  >
                    <Plus className="mr-1.5 h-3 w-3" />
                    Initialize Task
                  </Button>
                )}
              </div>
            ) : (
              tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  {...task}
                  status={statusKey}
                  isDragging={task.id === draggingTaskId}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('taskId', task.id.toString());

                    // Set drag image to the card itself and offset it nicely
                    // This makes the card "properly" follow the cursor
                    if (e.currentTarget) {
                      e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
                    }

                    onDragStart?.(task.id);
                  }}
                  onDragEnd={() => {
                    onDragEnd?.();
                  }}
                  onClick={() => onTaskClick?.(task.id)}
                  onEdit={() => onEditTask?.(task.id)}
                  onDelete={() => onDeleteTask?.(task.id)}
                  onDuplicate={() => onDuplicateTask?.(task.id)}
                  onAssigneeToggle={onAssigneeToggle}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
